import { Box } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { OnChangeFn, Row, SortingState } from "@tanstack/react-table"
import { getCoreRowModel } from "@tanstack/react-table"
import type { FunctionComponent } from "react"
import React, { useCallback, useEffect, useMemo, useRef } from "react"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { Location, LocationSortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { NotificationType } from "@/schemas/notification"
import type { SortableAccessorKey } from "@/screens/Companies/components/tables/LocationsTableUtils"
import {
  accessorKeyToLocationSortField,
  columns,
  locationSortFieldToAccessoryKey,
} from "@/screens/Companies/components/tables/LocationsTableUtils"
import type { LocationSearchFilters } from "@/screens/Companies/stores/useLocationSearchStore"
import { useLocationSearchStore } from "@/screens/Companies/stores/useLocationSearchStore"
import type { SearchCategory } from "@/screens/Companies/types/enums"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"

export const LocationsTable: FunctionComponent = () => {
  const navigate = useNavigate()
  const currentSearch = useSearch({ from: "/app/companies/" }) as LocationSearchFilters
  const { category, query, field, order, ...restSearchParams } = currentSearch

  const data = useLocationSearchStore(({ data }) => data)
  const fetching = useLocationSearchStore(({ fetching }) => fetching)
  const error = useLocationSearchStore(({ error }) => error)
  const executeSearch = useLocationSearchStore(({ executeSearch }) => executeSearch)
  const loadMore = useLocationSearchStore(({ loadMore }) => loadMore)
  const clearStore = useLocationSearchStore(({ clearStore }) => clearStore)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const searchFilters: LocationSearchFilters = useMemo(() => {
    const { types, ...otherSearchParams } = currentSearch
    return {
      companyTypes: types,
      ...otherSearchParams,
    }
  }, [currentSearch])

  useEffect(() => {
    executeSearch(queryClient, searchFilters)
  }, [executeSearch, searchFilters])

  const sorting = useMemo(() => {
    const tableSortField = locationSortFieldToAccessoryKey.get(field as LocationSortField) as string
    const tableSortOrder = order || SortingOrder.Asc
    return [
      {
        id: tableSortField,
        asc: tableSortOrder === SortingOrder.Asc,
        desc: tableSortOrder === SortingOrder.Desc,
      },
    ]
  }, [field, order])

  const handleSortingChange: OnChangeFn<SortingState> = (updaterFn) => {
    // @ts-expect-error - updaterFn is know fn
    const newSorting = updaterFn(sorting)

    if (newSorting.length > 0) {
      const sort = newSorting[0]
      const key = sort.id as SortableAccessorKey
      const field = accessorKeyToLocationSortField.get(key)
      const params = {
        ...restSearchParams,
        query,
        category: category as SearchCategory,
        field,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }

      const refinedParams = getRefinedSearchParams(params)
      void navigate({
        search: refinedParams.search,
        to: "/app/companies",
        replace: true,
      })
    }
  }

  const [throttledLoadMore] = useThrottle(() => loadMore(queryClient, searchFilters), 200)
  const onScroll = useScrollLoadMore({ fetching, loadMore: throttledLoadMore, scrollContainerRef: tableContainerRef })

  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  const handleRowClick = useCallback(
    (row: Row<Location>) => {
      if (!row.original.company?.companyId) {
        notify({
          message: "Failed to go to company details",
          type: NotificationType.Error,
        })
        return
      }
      navigate({ to: `/app/companies/${row.original.company.companyId}/edit` })
    },
    [navigate, notify]
  )

  return (
    <Box className='flex h-full flex-col'>
      <AerosBaseTable
        error={error}
        features={{
          table: {
            stickyHeader: true,
          },
          tanstackOptions: {
            data,
            columns,
            state: {
              sorting,
              isLoading: fetching,
            },
            manualSorting: true,
            enableSorting: true,
            enableMultiSort: false,
            enableSortingRemoval: false,
            getCoreRowModel: getCoreRowModel(),
            onSortingChange: handleSortingChange,
          },
        }}
        slotProps={{
          container: {
            ref: tableContainerRef,
            onScroll,
          },
          bodyRow: {
            onClick: (row) => {
              if ("original" in row) {
                handleRowClick(row)
              }
            },
            className: "hover:bg-primary-100 cursor-pointer",
          },
        }}
        noDataMessage='No locations found'
      />
    </Box>
  )
}
